import React, { useState } from 'react';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import './Auth.css';

function Auth({ onAuthSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!isSupabaseConfigured()) {
      setError('Supabase not configured. Please set up Supabase first.');
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        
        if (error) throw error;
        
        if (data.user && !data.user.email_confirmed_at) {
          setMessage('Check your email to confirm your account!');
        } else {
          onAuthSuccess?.(data.user);
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        onAuthSuccess?.(data.user);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (!isSupabaseConfigured()) {
      setError('Supabase not configured. Please set up Supabase first.');
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      
      if (error) throw error;
      // Note: The actual auth will complete in the callback page
      // The onAuthStateChange listener in App.js will handle the rest
    } catch (err) {
      setError(err.message || 'Google authentication failed');
    }
  };

  const handleSpotifyAuth = async () => {
    // Use existing Spotify OAuth flow
    const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
    
    try {
      const response = await fetch(`${API_URL}/spotify-login`);
      if (!response.ok) throw new Error('Failed to initiate Spotify login');
      
      const data = await response.json();
      if (data.auth_url) {
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'Spotify Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        window.addEventListener('message', async (event) => {
          if (event.data === 'spotify-login-success') {
            if (popup) popup.close();
            // After Spotify login, create/update Supabase user and analyze taste
            await handleSpotifyAuthSuccess();
          } else if (event.data === 'spotify-login-error') {
            if (popup) popup.close();
            setError('Spotify authentication failed');
          }
        }, { once: true });
      }
    } catch (err) {
      setError(err.message || 'Spotify authentication failed');
    }
  };

  const handleSpotifyAuthSuccess = async () => {
    // Get Spotify user info and create Supabase user
    const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
    
    try {
      // Get Spotify user data from backend
      const response = await fetch(`${API_URL}/api/spotify/user`, {
        credentials: 'include', // Include session cookies
      });
      
      if (response.ok && isSupabaseConfigured()) {
        const spotifyData = await response.json();
        
        // Check if user is already signed in to Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session && spotifyData.email) {
          // User not signed in to Supabase, create account with email
          // Note: This is a simplified flow - in production you'd want proper OAuth linking
          const { error: signUpError } = await supabase.auth.signUp({
            email: spotifyData.email,
            password: Math.random().toString(36).slice(-12), // Random password
            options: {
              emailRedirectTo: window.location.origin
            }
          });
          
          if (signUpError && !signUpError.message.includes('already registered')) {
            console.error('Error creating Supabase user:', signUpError);
          }
        }
        
        // Sync Spotify data to Supabase (this will also analyze taste)
        if (session || spotifyData.email) {
          const userId = session?.user?.id || spotifyData.email; // Fallback to email if no session
          await fetch(`${API_URL}/api/spotify/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ user_id: userId })
          });
        }
      }
      
      // Trigger auth success callback
      onAuthSuccess?.();
    } catch (err) {
      console.error('Error linking Spotify to Supabase:', err);
      // Still call success since Spotify auth worked
      onAuthSuccess?.();
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{isSignUp ? 'Sign Up' : 'Sign In'}</h2>
        
        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-message">{message}</div>}
        
        <form onSubmit={handleEmailAuth} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              minLength={6}
            />
          </div>
          
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </form>
        
        <div className="auth-divider">
          <span>or</span>
        </div>
        
        <div className="auth-providers">
          <button 
            onClick={handleGoogleAuth} 
            className="auth-provider-btn google"
            disabled={!isSupabaseConfigured()}
          >
            <span className="provider-icon">🔵</span>
            Continue with Google
          </button>
          
          <button 
            onClick={handleSpotifyAuth} 
            className="auth-provider-btn spotify"
          >
            <span className="provider-icon">🎵</span>
            Continue with Spotify
          </button>
        </div>
        
        <div className="auth-switch">
          {isSignUp ? (
            <p>
              Already have an account?{' '}
              <button 
                type="button" 
                onClick={() => setIsSignUp(false)}
                className="link-button"
              >
                Sign In
              </button>
            </p>
          ) : (
            <p>
              Don't have an account?{' '}
              <button 
                type="button" 
                onClick={() => setIsSignUp(true)}
                className="link-button"
              >
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Auth;
